import { createContext, type ReactNode } from 'react'
import { toast } from 'sonner'
import * as federationsService from '~/services/federations'
import type {
  CreateFederationRequest,
  Federation,
  UpdateFederationRequest,
} from '~/types/federation'

interface FederationsContextType {
  listFederations: () => Promise<Federation[]>
  getFederation: (id: number) => Promise<Federation | null>
  createFederation: (
    data: CreateFederationRequest
  ) => Promise<Federation | null>
  updateFederation: (
    id: number,
    data: UpdateFederationRequest
  ) => Promise<Federation | null>
  deleteFederation: (id: number) => Promise<boolean>
}

export const FederationsContext = createContext<
  FederationsContextType | undefined
>(undefined)

interface FederationsProviderProps {
  children: ReactNode
}

export function FederationsProvider({ children }: FederationsProviderProps) {
  const listFederations = async (): Promise<Federation[]> => {
    try {
      const response = await federationsService.listFederations()

      if (response.success) {
        return response.data
      } else {
        toast.error(response.error || 'Erro ao listar federações')
        return []
      }
    } catch (error) {
      toast.error('Erro ao listar federações')
      console.error('List federations error:', error)
      return []
    }
  }

  const getFederation = async (id: number): Promise<Federation | null> => {
    try {
      const response = await federationsService.getFederation(id)

      if (response.success) {
        return response.data
      } else {
        toast.error(response.error || 'Erro ao buscar federação')
        return null
      }
    } catch (error) {
      toast.error('Erro ao buscar federação')
      console.error('Get federation error:', error)
      return null
    }
  }

  const createFederation = async (
    data: CreateFederationRequest
  ): Promise<Federation | null> => {
    try {
      const response = await federationsService.createFederation(data)

      if (response.success) {
        toast.success('Federação criada com sucesso!')
        return response.data.federacao
      } else {
        toast.error(response.error || 'Erro ao criar federação')
        return null
      }
    } catch (error) {
      toast.error('Erro ao criar federação')
      console.error('Create federation error:', error)
      return null
    }
  }

  const updateFederation = async (
    id: number,
    data: UpdateFederationRequest
  ): Promise<Federation | null> => {
    try {
      const response = await federationsService.updateFederation(id, data)

      if (response.success) {
        toast.success('Federação atualizada com sucesso!')
        return response.data.federacao
      } else {
        toast.error(response.error || 'Erro ao atualizar federação')
        return null
      }
    } catch (error) {
      toast.error('Erro ao atualizar federação')
      console.error('Update federation error:', error)
      return null
    }
  }

  const deleteFederation = async (id: number): Promise<boolean> => {
    try {
      const response = await federationsService.deleteFederation(id)

      if (response.success) {
        toast.success('Federação excluída com sucesso!')
        return true
      } else {
        toast.error(response.error || 'Erro ao excluir federação')
        return false
      }
    } catch (error) {
      toast.error('Erro ao excluir federação')
      console.error('Delete federation error:', error)
      return false
    }
  }

  return (
    <FederationsContext.Provider
      value={{
        listFederations,
        getFederation,
        createFederation,
        updateFederation,
        deleteFederation,
      }}
    >
      {children}
    </FederationsContext.Provider>
  )
}
